import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../config/api";

const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;
const DEFAULT_ROOT_MARGIN = "300px 0px";

const clearSession = () => {
  ["access", "authCredentials", "refresh", "role", "email"].forEach((key) =>
    localStorage.removeItem(key),
  );
};

const withPage = (endpoint, page) => {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set("page", String(Math.max(1, Number(page) || 1)));
  return url.toString();
};

const requestPage = async (endpoint, page, navigate, signal, authenticated) => {
  const url = withPage(endpoint, page);
  const authenticatedOptions = () => ({
    method: "GET",
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });
  let response = await fetch(
    url,
    authenticated ? authenticatedOptions() : { method: "GET", signal },
  );

  if (!authenticated) {
    if (!response.ok) throw new Error(`https error! Status: ${response.status}`);
    return response.json();
  }

  if (response.status === 401) {
    const refreshed = await fetch(REFRESH_URL, {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: localStorage.getItem("refresh") }),
    });
    if (!refreshed.ok) {
      clearSession();
      navigate?.("/signin");
      return null;
    }
    localStorage.setItem("access", (await refreshed.json()).access);
    response = await fetch(url, authenticatedOptions());
  }

  if (!response.ok) throw new Error(`https error! Status: ${response.status}`);
  return response.json();
};

export const appendUnique = (current = [], incoming = [], getKey) => {
  const keyOf = getKey || ((item) => item?.id ?? item?.uuid ?? item?.code);
  const seen = new Set(current.map(keyOf));
  return [
    ...current,
    ...incoming.filter((item) => {
      const key = keyOf(item);
      if (key == null || seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  ];
};

export const mergeBuckets = (bucketNames, getKey) => (current, incoming) =>
  bucketNames.reduce(
    (result, bucket) => ({
      ...result,
      [bucket]: appendUnique(current?.[bucket], incoming?.[bucket], getKey),
    }),
    current || {},
  );

export const mergeNestedBuckets = (groups, bucketNames, getKey) =>
  (current, incoming) =>
    groups.reduce(
      (result, group) => ({
        ...result,
        [group]: mergeBuckets(bucketNames, getKey)(current?.[group], incoming?.[group]),
      }),
      current || {},
    );

export const normalizePaginationMeta = (response, requestedPage) => {
  const parsedPage = Number(response?.page);
  const parsedTotalPages = Number(response?.total_pages);
  const parsedCount = Number(response?.count);
  const parsedPageSize = Number(response?.page_size);
  const inferredTotalPages =
    Number.isFinite(parsedCount) && Number.isFinite(parsedPageSize) && parsedPageSize > 0
      ? Math.ceil(Math.max(0, parsedCount) / parsedPageSize)
      : 0;
  return {
    page: Math.max(
      1,
      Number.isFinite(parsedPage) ? parsedPage : Number(requestedPage) || 1,
    ),
    totalPages: Number.isFinite(parsedTotalPages)
      ? Math.max(0, parsedTotalPages)
      : inferredTotalPages,
    next: response?.next,
  };
};

const getHasNext = ({ page, totalPages, next }) => {
  if (next === null) return false;
  if (next !== undefined) return true;
  return page < totalPages;
};

const getScrollPosition = (root) =>
  root ? root.scrollTop : window.scrollY || document.documentElement.scrollTop || 0;

export default function useInfinitePaginated({
  endpoint,
  navigate,
  initialData = [],
  select = (response) => response?.results || [],
  merge = appendUnique,
  enabled = true,
  authenticated = true,
  rootMargin = DEFAULT_ROOT_MARGIN,
}) {
  const [data, setData] = useState(initialData);
  const [initialLoading, setInitialLoading] = useState(enabled);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setErrorState] = useState(null);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState({ page: 0, totalPages: 0, next: undefined });
  const [sentinelNode, setSentinelNode] = useState(null);
  const [scrollRootNode, setScrollRootNode] = useState(null);

  const fetchingRef = useRef(false);
  const metaRef = useRef(meta);
  const hasNextRef = useRef(false);
  const generationRef = useRef(0);
  const controllerRef = useRef(null);
  const failedPageRef = useRef(null);
  const committedPageRef = useRef(0);
  const armedRef = useRef(true);
  const userScrolledRef = useRef(false);
  const wasIntersectingRef = useRef(false);
  const lastScrollPositionRef = useRef(0);
  const scrollRootNodeRef = useRef(null);
  const initialRef = useRef(initialData);
  const selectRef = useRef(select);
  const mergeRef = useRef(merge);

  initialRef.current = initialData;
  selectRef.current = select;
  mergeRef.current = merge;

  const sentinelRef = useCallback((node) => setSentinelNode(node), []);
  const scrollRootRef = useCallback((node) => {
    scrollRootNodeRef.current = node;
    setScrollRootNode(node);
  }, []);

  const resetObserverGate = useCallback((root = null) => {
    armedRef.current = true;
    userScrolledRef.current = false;
    wasIntersectingRef.current = false;
    lastScrollPositionRef.current = getScrollPosition(root);
  }, []);

  const loadPage = useCallback(
    async (requestedPage, { force = false } = {}) => {
      if (!enabled || fetchingRef.current) return false;
      const page = Math.max(1, Number(requestedPage) || 1);

      if (!force) {
        if (page <= committedPageRef.current) return false;
        if (committedPageRef.current > 0 && !hasNextRef.current) return false;
        if (page !== committedPageRef.current + 1) return false;
      }

      const generation = generationRef.current;
      fetchingRef.current = true;
      failedPageRef.current = null;
      setErrorState(null);
      if (page === 1) {
        setInitialLoading(true);
        setLoadingMore(false);
      } else {
        armedRef.current = false;
        userScrolledRef.current = false;
        setLoadingMore(true);
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const response = await requestPage(
          endpoint,
          page,
          navigate,
          controller.signal,
          authenticated,
        );
        if (!response || generation !== generationRef.current) return false;

        const nextMeta = normalizePaginationMeta(response, page);
        metaRef.current = nextMeta;
        hasNextRef.current = getHasNext(nextMeta);
        committedPageRef.current = nextMeta.page;
        setMeta(nextMeta);
        if (page === 1 || response?.summary !== undefined) {
          setSummary(response?.summary ?? null);
        }
        const selected = selectRef.current(response);
        setData((current) =>
          page === 1 ? selected : mergeRef.current(current, selected),
        );
        return true;
      } catch (requestError) {
        if (requestError.name !== "AbortError" && generation === generationRef.current) {
          failedPageRef.current = page;
          setErrorState(requestError);
        }
        return false;
      } finally {
        if (generation === generationRef.current) {
          fetchingRef.current = false;
          controllerRef.current = null;
          setInitialLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [authenticated, enabled, endpoint, navigate],
  );

  const resetAndLoad = useCallback(() => {
    generationRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    fetchingRef.current = false;
    failedPageRef.current = null;
    committedPageRef.current = 0;
    const resetMeta = { page: 0, totalPages: 0, next: undefined };
    metaRef.current = resetMeta;
    hasNextRef.current = false;
    setMeta(resetMeta);
    setData(initialRef.current);
    setSummary(null);
    setErrorState(null);
    setLoadingMore(false);
    resetObserverGate(scrollRootNodeRef.current);

    if (enabled) {
      setInitialLoading(true);
      void loadPage(1, { force: true });
    } else {
      setInitialLoading(false);
    }
  }, [enabled, loadPage, resetObserverGate]);

  useEffect(() => {
    resetAndLoad();
    return () => {
      generationRef.current += 1;
      controllerRef.current?.abort();
      controllerRef.current = null;
      fetchingRef.current = false;
    };
  }, [resetAndLoad]);

  const requestNextFromScroll = useCallback(() => {
    if (
      !armedRef.current ||
      !userScrolledRef.current ||
      fetchingRef.current ||
      failedPageRef.current ||
      !hasNextRef.current
    ) return;

    armedRef.current = false;
    userScrolledRef.current = false;
    void loadPage(metaRef.current.page + 1);
  }, [loadPage]);

  useEffect(() => {
    if (!sentinelNode || !enabled) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = Boolean(entry?.isIntersecting);
        wasIntersectingRef.current = isIntersecting;
        if (!isIntersecting) {
          if (!fetchingRef.current) armedRef.current = true;
          userScrolledRef.current = false;
          return;
        }
        requestNextFromScroll();
      },
      { root: scrollRootNode || null, rootMargin },
    );

    // Scroll events do not bubble. When no explicit root is supplied, listen in
    // the capture phase so page-level overflow containers still provide the
    // user-intent signal required to arm loading.
    const scrollTarget = scrollRootNode || document;
    lastScrollPositionRef.current = getScrollPosition(scrollRootNode);
    const handleScroll = (event) => {
      const eventTarget = event?.target;
      const nextPosition = scrollRootNode
        ? getScrollPosition(scrollRootNode)
        : eventTarget && eventTarget !== document
          ? eventTarget.scrollTop || 0
          : getScrollPosition(null);
      const movedTowardEnd = nextPosition > lastScrollPositionRef.current;
      lastScrollPositionRef.current = nextPosition;
      if (!movedTowardEnd) return;
      userScrolledRef.current = true;
      if (wasIntersectingRef.current) requestNextFromScroll();
    };

    observer.observe(sentinelNode);
    scrollTarget.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: !scrollRootNode,
    });
    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener("scroll", handleScroll, !scrollRootNode);
    };
  }, [enabled, requestNextFromScroll, rootMargin, scrollRootNode, sentinelNode]);

  const retry = useCallback(() => {
    if (!failedPageRef.current) return;
    void loadPage(failedPageRef.current, { force: true });
  }, [loadPage]);
  const reload = useCallback(() => resetAndLoad(), [resetAndLoad]);
  const loadNext = useCallback(() => {
    if (!hasNextRef.current || fetchingRef.current || failedPageRef.current) {
      return false;
    }
    return loadPage(metaRef.current.page + 1);
  }, [loadPage]);
  const setError = useCallback((nextError) => {
    failedPageRef.current = null;
    setErrorState(nextError);
  }, []);

  return {
    data,
    setData,
    initialLoading,
    loadingMore,
    error,
    summary,
    setError,
    hasNext: getHasNext(meta),
    sentinelRef,
    scrollRootRef,
    retry,
    reload,
    loadNext,
  };
}
